"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { type CSSProperties, forwardRef, type HTMLAttributes } from "react";

import { cn } from "@/lib/utils";

const typographyVariants = cva("", {
  variants: {
    variant: {
      display01: "text-[96px] leading-[120%] font-[600]",
      title01:   "text-[36px] leading-[140%] font-[700]",
      title02:   "text-[32px] leading-[140%] font-[700]",
      title03:   "text-[28px] leading-[140%] font-[700]",
      headline01:"text-[20px] leading-[140%] font-[600]",
      headline02:"text-[20px] leading-[140%] font-[500]",
      headline03:"text-[16px] leading-[140%] font-[600]",
      headline04:"text-[16px] leading-[140%] font-[500]",
      body01:    "text-[14px] leading-[160%] font-[600]",
      body02:    "text-[14px] leading-[160%] font-[500]",
      label01:   "text-[15px] leading-[140%] font-[600]",
      label02:   "text-[15px] leading-[140%] font-[500]",
      label03:   "text-[14px] leading-[140%] font-[600]",
      label04:   "text-[14px] leading-[140%] font-[500]",
      label05:   "text-[12px] leading-[140%] font-[600]",
      label06:   "text-[12px] leading-[140%] font-[500]",
      label07:   "text-[10px] leading-[140%] font-[600]",
      label08:   "text-[10px] leading-[140%] font-[500]",
      label09:   "text-[15px] leading-[140%] font-[500]",
      label10:   "text-[10px] leading-[140%] font-[600]",
    },
  },
  defaultVariants: {
    variant: "label04",
  },
});

export interface TypoGraphProps
  extends HTMLAttributes<HTMLElement>,
    VariantProps<typeof typographyVariants> {
  as?: "p" | "span" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "label" | "div";
  noOfLines?: number;
}

const TypoGraph = forwardRef<HTMLElement, TypoGraphProps>(
  ({ className, variant, as: Tag = "p", noOfLines, style, ...props }, ref) => {
    const clampStyle: CSSProperties | undefined =
      noOfLines != null
        ? {
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: noOfLines,
            overflow: "hidden",
          }
        : undefined;

    return (
      <Tag
        ref={ref as React.RefCallback<HTMLElement>}
        className={cn(typographyVariants({ variant }), className)}
        style={{ ...style, ...clampStyle }}
        {...props}
      />
    );
  }
);
TypoGraph.displayName = "TypoGraph";

export { TypoGraph, typographyVariants };
